import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useNewApiCall } from 'hooks';
import { Button } from 'primereact/button';
import { TabPanel, TabView } from 'primereact/tabview';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { generateGUID } from 'utils';
import { markDeletedItems } from 'utils/markDeletedItems';

import { CalendarService } from 'components/services';
import { ComplexRoleService } from 'components/services/ComplexRoleService';
import { ScheduleService } from 'components/services/ScheduleService';
import { TaskService } from 'components/services/TaskService';
import { BaseFiasUrl } from 'constants/BaseUrl';
import { DepartmentCalendarTemplate } from 'constants/DepartmentCalendarTemplate';
import { PickerScheduleTemplate } from 'constants/PickerScheduleTemplate';
import { CustomDropdown } from 'ui/custom-controls';
import TagsFrame from 'ui/shared/TagsFrame';

import { DepartmentsService } from '../../components/services/DepartmentsService';
import {
  DetailedInformationFormType,
  DetailedInformationSchema
} from '../../constants/schemas';
import '../css/DetailedInformationCard.css';
import {
  ComplexRoleUI,
  DepartmentCalendar,
  DepartmentTaskType,
  DetailedInformationCardProps,
  DropdownObjectType,
  DropdownSelectorType,
  FiasObject,
  ObjectForSelection,
  ScheduleWithDates,
  TreeNode
} from '../../types/interfaces';
import DepartmentAssignmentScheduleDialog, {
  DepartmentAssignmentSchedule
} from '../DepartmentAssignmentScheduleDialog';
import { TagsComponent } from '../TagsComponent';
import { TimeViewComponent } from '../TimeViewComponent';

import LocationTabContent from './LocationTabContent';
import MainTab from './MainTab';
import TaskTypeEffortDialog from './TaskTypeEffortDialog';
import { useGeographicalZonesCardHandlers } from './useGeographicalZonesCardHandlers';

const DetailedInformationCard = (
  detailedInformationCardProps: DetailedInformationCardProps
) => {
  const { updateDetails, onAdd, onGeographicalZoneClick, ...data } =
    detailedInformationCardProps;

  const hasChildren =
    data.children &&
    data.children.some((child: any) => child.data !== 'Сотрудник');

  const methods = useForm<DetailedInformationFormType>({
    resolver: zodResolver(DetailedInformationSchema),
    defaultValues: data as DetailedInformationFormType
  });

  const {
    register,
    unregister,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors, isDirty }
  } = methods;
  const parentKey = watch('parentKey');
  const userRole = watch('userRole');
  const taskTypes = watch('taskTypes');
  const departmentCalendars = watch('departmentCalendars');
  const id = watch('id');
  const isFias = watch('isFias');
  const cityFias = watch('cityFias');
  const houseFias = watch('houseFias');
  const streetFias = watch('streetFias');
  const customCity = watch('customCity');
  const customStreet = watch('customStreet');
  const customHouse = watch('customHouse');
  const latitude = watch('latitude');
  const longitude = watch('longitude');
  const region = watch('region');
  const county = watch('county');
  const district = watch('district');
  const geographicalZonesRaw = watch('geographicalZones') as
    | ObjectForSelection[]
    | undefined;
  const geographicalZones = useMemo(
    () => geographicalZonesRaw ?? [],
    [geographicalZonesRaw]
  );
  const departmentSchedules = watch('departmentSchedules');
  const useEmployeeAssignmentSchedule = watch('useEmployeeAssignmentSchedule');
  const employeeAssignmentScheduleSetting = watch(
    'employeeAssignmentScheduleSetting'
  );

  const [calendarsOptions, setCalendarsOptions] = useState<TreeNode[]>([]);
  const [scheduleыOptions, setScheduleOptions] = useState<TreeNode[]>([]);
  const [scrollHeight, setScrollHeight] = useState('100vh');
  const [halfScrollHeight, setHalfScrollHeight] = useState('50vh');
  const [taskTypesOptions, setTaskTypesOptions] = useState<
    DepartmentTaskType[]
  >([]);
  const [localTaskTypes, setLocalTaskTypes] = useState<DepartmentTaskType[]>(
    []
  );
  const [complexRoleOptions, setComplexRoleOptions] = useState<ComplexRoleUI[]>(
    []
  );
  const [localUserRole, setLocalUserRole] = useState<ComplexRoleUI[]>([]);
  const [localUserRoleTags, setLocalUserRoleTags] = useState<
    DropdownSelectorType[]
  >([]);
  const [geographicalZonesOptions, setGeographicalZonesOptions] = useState<
    ObjectForSelection[]
  >([]);
  const [houses, setHouses] = useState<FiasObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [localDirty, setLocalDirty] = useState<boolean>(false);
  const [editingTaskTypeId, setEditingTaskTypeId] = useState<
    string | number | null
  >(null);
  const [isTaskTypeEffortDialogOpen, setIsTaskTypeEffortDialogOpen] =
    useState(false);
  const [
    showEmployeeAssignmentScheduleDialog,
    setShowEmployeeAssignmentScheduleDialog
  ] = useState(false);

  const itemType = data.data;
  const treeRef = useRef(null);
  // Блок расписания сохраняется своими эндпоинтами — шлём их только если его трогали
  const scheduleDirtyRef = useRef(false);
  const [scheduleUnsaved, setScheduleUnsaved] = useState(false);

  const {
    refreshGeographicalZonesOptions,
    handleGeographicalZonesChange,
    handleDeleteGeographicalZoneTag
  } = useGeographicalZonesCardHandlers({
    getValues,
    setValue,
    geographicalZones,
    setGeographicalZonesOptions,
    setLocalDirty
  });

  const locationTabErrors = useMemo(() => {
    const hasErrors =
      errors.isFias ||
      errors.cityFias ||
      errors.streetFias ||
      errors.houseFias ||
      errors.customCity ||
      errors.customStreet ||
      errors.customHouse ||
      errors.region ||
      errors.county ||
      errors.district ||
      errors.latitude ||
      errors.longitude;

    return Boolean(hasErrors);
  }, [
    errors.isFias,
    errors.cityFias,
    errors.streetFias,
    errors.houseFias,
    errors.customCity,
    errors.customStreet,
    errors.customHouse,
    errors.region,
    errors.county,
    errors.district,
    errors.latitude,
    errors.longitude
  ]);

  const calendarsAndSchedulesErrors = useMemo(() => {
    if (
      errors.departmentCalendars ||
      errors.departmentSchedules ||
      errors.useEmployeeAssignmentSchedule
    ) {
      return true;
    } else {
      return false;
    }
  }, [
    errors.departmentCalendars,
    errors.departmentSchedules,
    errors.useEmployeeAssignmentSchedule
  ]);

  const employeeAssignmentScheduleStatusText = useMemo(() => {
    if (errors.useEmployeeAssignmentSchedule) {
      return errors.useEmployeeAssignmentSchedule.message;
    }
    if (scheduleUnsaved) {
      return 'Сохраните карточку участка для применения настроек расписания';
    }
    return useEmployeeAssignmentSchedule
      ? 'Распределение по расписанию включено'
      : 'Распределение по расписанию не настроено';
  }, [
    errors.useEmployeeAssignmentSchedule,
    scheduleUnsaved,
    useEmployeeAssignmentSchedule
  ]);

  const handleInputChange = useCallback(
    async (field: keyof DetailedInformationFormType) => {
      setLocalDirty(true);
      await trigger(field as any);
    },
    [trigger]
  );

  const handleEditTaskType = (taskTypeId: string | number) => {
    setEditingTaskTypeId(taskTypeId);
    setIsTaskTypeEffortDialogOpen(true);
  };

  const setValueDirty = useMemo(() => {
    return ((name: any, value: any, options?: any) => {
      setLocalDirty(true);
      return setValue(name, value, {
        shouldDirty: true,
        shouldValidate: true,
        ...(options ?? {})
      });
    }) as typeof setValue;
  }, [setValue]);

  useEffect(() => {
    setLocalUserRole(userRole || []);
    setLocalUserRoleTags(
      (userRole || []).map((role) => ({
        id: typeof role.id === 'number' ? role.id.toString() : role.id,
        label: role.name
      }))
    );
  }, [userRole]);

  useEffect(() => {
    setLocalTaskTypes(taskTypes || []);
  }, [taskTypes]);

  useEffect(() => {
    detailedInformationCardProps.onDirtyChange?.(localDirty || isDirty);
  }, [localDirty, isDirty, detailedInformationCardProps.onDirtyChange]);

  useEffect(() => {
    // Ensure RHF tracks dirty state for fields that are mainly updated via setValue()
    // (custom controls like FIAS inputs, calendars/schedules editors, role picker, etc.)
    const fieldsToRegister: Array<keyof DetailedInformationFormType> = [
      'userRole',
      'isFias',
      'cityFias',
      'streetFias',
      'houseFias',
      'customCity',
      'customStreet',
      'customHouse',
      'region',
      'county',
      'district',
      'productivity',
      'priority',
      'isCustomProductivity',
      'latitude',
      'longitude',
      'geographicalZones',
      'departmentCalendars',
      'departmentSchedules',
      'isIntervalType',
      'intervalDuration',
      'useEmployeeAssignmentSchedule',
      'employeeAssignmentScheduleSetting'
    ];

    fieldsToRegister.forEach((f) => register(f));
    return () => {
      fieldsToRegister.forEach((f) => unregister(f));
    };
  }, [register, unregister]);

  const getControlsOptions = useCallback(async () => {
    const fetchSchedules = async () => {
      if (complexRoleOptions.length === 0) {
        const resultComplexRoleOptions =
          await ComplexRoleService.getComplexRolesForSelection();
        setComplexRoleOptions(resultComplexRoleOptions.data.value);
      }
    };

    const fetchTaskTypes = async () => {
      const types = await TaskService.getTaskTypes();
      if (types?.data) {
        const transformedTypes = types.data.map((type: DropdownObjectType) => ({
          id: Number(type.key),
          label: type.label
        }));

        setTaskTypesOptions(transformedTypes);
      }
    };

    fetchTaskTypes();
    fetchSchedules();
  }, []);

  useNewApiCall(getControlsOptions, []);

  const fetchHouses = async (streetId: number, passedCityId?: number) => {
    setLoading(true);
    const response = await fetch(
      `${BaseFiasUrl}/city/${
        passedCityId ?? cityFias!.objectId
      }/street/${streetId}/house`
    );
    const data = await response.json();
    setHouses(data);
    setLoading(false);
  };

  const fetchAndResetDepartmentData = async (departmentId: number) => {
    try {
      // Настройки расписания живут в отдельном ресурсе, тянем их параллельно с участком
      const [result, scheduleSetting] = await Promise.all([
        DepartmentsService.getDepartmentById(departmentId),
        DepartmentsService.getDepartmentScheduleSetting(departmentId)
          .then((resp) => resp.data)
          .catch(() => null)
      ]);
      const departmentData = result.data;

      if (departmentData) {
        // Определяем режим ФИАС
        const isFiasMode = !!departmentData.cityFias;

        // Сброс формы с учетом режима ФИАС
        reset({
          ...data,
          label: departmentData.name,
          parentKey: departmentData.parentId?.toString() || '',
          userRole: departmentData.userRole || [],
          taskTypes: departmentData.taskTypes || [],
          departmentCalendars: departmentData.departmentCalendars || [],
          departmentSchedules: departmentData.departmentSchedules || [],
          cityFias: departmentData.cityFias || null,
          streetFias: departmentData.streetFias || undefined,
          houseFias: departmentData.houseFias || null,
          customCity: departmentData.customCity || '',
          customStreet: departmentData.customStreet || '',
          customHouse: departmentData.customHouse || '',
          isFias: isFiasMode, // Устанавливаем флаг на основе наличия cityFias
          latitude: departmentData.latitude || null,
          longitude: departmentData.longitude || null,
          region: departmentData.region || '',
          district: departmentData.district,
          county: departmentData.county,
          productivity: departmentData.productivity || '',
          priority: departmentData.priority || 0,
          isCustomProductivity: departmentData.isCustomProductivity || false,
          geographicalZones: departmentData.geographicalZones ?? [],
          isIntervalType: departmentData.isIntervalType ?? false,
          intervalDuration: departmentData.intervalDuration ?? undefined,
          useEmployeeAssignmentSchedule:
            departmentData.useEmployeeAssignmentSchedule ?? false,
          employeeAssignmentScheduleSetting: scheduleSetting ?? null
        });
        setLocalDirty(false);
        scheduleDirtyRef.current = false;
        setScheduleUnsaved(false);

        // Загружаем дома если есть улица и город
        if (departmentData.streetFias && departmentData.cityFias) {
          fetchHouses(
            departmentData.streetFias.objectId,
            departmentData.cityFias.objectId
          );
        }
      }
    } catch (error) {
      console.error('Error fetching department data:', error);
    }
  };

  const getDetailedInfoCardApi = useCallback(async () => {
    if (itemType === 'Участок') {
      const fetchCalendarsAndSchedules = async () => {
        const calendars = await CalendarService.getForSelection();
        setCalendarsOptions(calendars.data.value ?? []);
        const schedules = await ScheduleService.getSchedulesForSelection();
        setScheduleOptions(schedules.data.value ?? []);

        await refreshGeographicalZonesOptions();
      };
      fetchCalendarsAndSchedules();
    }
    if (itemType === 'Участок' && id) {
      fetchAndResetDepartmentData(parseInt(id));
    }
  }, [id, itemType, refreshGeographicalZonesOptions]); // Run when id or data type changes

  useNewApiCall(getDetailedInfoCardApi, [id, itemType]);

  const handleSaveWeight = useCallback(
    (taskTypeId: string | number, weight: number) => {
      setLocalDirty(true);

      const updatedTaskTypes = (taskTypes || []).map((tt: any) => {
        if (tt.id === taskTypeId) {
          return { ...tt, effort: weight };
        }
        return tt;
      });

      setValue('taskTypes', updatedTaskTypes, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [taskTypes, setValue]
  );

  const onSubmit: SubmitHandler<DetailedInformationFormType> = async (data) => {
    try {
      if (
        data.isIntervalType &&
        (data.intervalDuration === undefined ||
          data.intervalDuration === null ||
          data.intervalDuration === 0)
      ) {
        if (detailedInformationCardProps.onError) {
          detailedInformationCardProps.onError(
            'Введите продолжительность интервала'
          );
        }
        return;
      }

      const commonData = {
        name: data.label,
        parentId: parentKey ? parseInt(parentKey) : undefined,
        taskTypes: taskTypes ?? [],
        userRole: userRole ?? [],
        isFias: Boolean(cityFias),
        productivity: data.productivity === '' ? 0 : data.productivity,
        priority: data.priority,
        isCustomProductivity: data.isCustomProductivity ?? false,
        isIntervalType: data.isIntervalType ?? false,
        intervalDuration: data.isIntervalType
          ? data.intervalDuration ?? undefined
          : undefined
      };

      const locationData =
        itemType === 'Участок'
          ? {
              cityFias,
              houseFias,
              streetFias,
              customCity,
              customStreet,
              customHouse,
              latitude,
              longitude,
              region,
              county,
              district,
              geographicalZones: geographicalZones ?? []
            }
          : {};

      if (updateDetails && id) {
        const result = await DepartmentsService.updateDepartment(parseInt(id), {
          id: parseInt(id),
          departmentCalendars,
          departmentSchedules,
          ...commonData,
          ...locationData
        });

        if (result.status >= 400) {
          if (result.status === 400 && result.data?.error === 'SameName') {
            if (detailedInformationCardProps.onError) {
              detailedInformationCardProps.onError(
                'Такое название уже существует'
              );
            }
            return;
          } else {
            throw new Error(`HTTP Error: ${result.status}`);
          }
        }

        await persistEmployeeAssignmentSchedule(parseInt(id));

        reset(getValues());
        setLocalDirty(false);

        updateDetails(data);
        await refreshGeographicalZonesOptions();
        await fetchAndResetDepartmentData(parseInt(id));
      } else if (onAdd) {
        const response = await DepartmentsService.createDepartment({
          departmentCalendars,
          departmentSchedules,
          ...commonData,
          ...locationData
        });

        if (response.status >= 400) {
          if (response.status === 400 && response.data?.error === 'SameName') {
            if (detailedInformationCardProps.onError) {
              detailedInformationCardProps.onError(
                'Такое название уже существует'
              );
            }
            return;
          } else {
            throw new Error(
              `HTTP Error: ${response.status} - ${response.data?.error}`
            );
          }
        }

        onAdd(response.data.value);

        const createdDepartmentId = response.data.value?.id;
        if (createdDepartmentId) {
          await persistEmployeeAssignmentSchedule(createdDepartmentId);
        }

        // См. комментарий в ветке обновления: снимаем «грязноту» сразу после подтверждения
        reset(getValues());
        setLocalDirty(false);

        await fetchAndResetDepartmentData(parseInt(id!));
      }
    } catch (error: any) {
      if (detailedInformationCardProps.onError) {
        detailedInformationCardProps.onError(error.message);
      }
    }
  };

  const handleSelectionChange = (
    selection: ComplexRoleUI | ComplexRoleUI[] | null
  ) => {
    setLocalDirty(true);
    setValue('userRole', selection as ComplexRoleUI[], {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  const handleTskTypesSelectionChange = (
    selection: DepartmentTaskType | DepartmentTaskType[] | null
  ) => {
    setLocalDirty(true);
    setValue('taskTypes', Array.isArray(selection) ? selection : [], {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  const handleDeleteRole = (taskId: string | number) => {
    setLocalDirty(true);
    const newRole = userRole?.filter(
      (role) => role.id!.toString() !== taskId.toString()
    );
    setValue('userRole', newRole, { shouldDirty: true, shouldValidate: true });
  };

  const handleDeleteTaskType = (taskId: string | number) => {
    setLocalDirty(true);
    const newTaskTypes = taskTypes?.filter(
      (taskType) => taskType.id!.toString() !== taskId.toString()
    );
    setValue('taskTypes', newTaskTypes, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  const persistEmployeeAssignmentSchedule = async (departmentId: number) => {
    if (itemType !== 'Участок' || !scheduleDirtyRef.current) return;

    try {
      const setting = getValues('employeeAssignmentScheduleSetting');
      if (setting) {
        await DepartmentsService.updateDepartmentScheduleSetting(departmentId, {
          ...setting,
          id: setting.id ?? 0,
          departmentId
        });
      }
      await DepartmentsService.updateDepartmentScheduleStatus(
        departmentId,
        getValues('useEmployeeAssignmentSchedule') ?? false
      );
      scheduleDirtyRef.current = false;
      setScheduleUnsaved(false);
    } catch (error) {
      throw new Error(
        'Не удалось сохранить настройки расписания распределения нарядов на сотрудников'
      );
    }
  };

  const handleEmployeeAssignmentScheduleSave = (
    data: DepartmentAssignmentSchedule | null,
    enabled: boolean
  ) => {
    scheduleDirtyRef.current = true;
    setScheduleUnsaved(true);
    setValueDirty('useEmployeeAssignmentSchedule', enabled);
    if (data) {
      setValueDirty('employeeAssignmentScheduleSetting', {
        ...(employeeAssignmentScheduleSetting ?? {}),
        ...data
      });
    }
    trigger('useEmployeeAssignmentSchedule');
  };

  const fetchEmployeeAssignmentNextRun = useCallback(async () => {
    if (!id) return null;
    const response = await DepartmentsService.getDepartmentScheduleNextRun(
      parseInt(id)
    );
    if (response.data.isSuccess && response.data.value) {
      return new Date(response.data.value);
    }
    return null;
  }, [id]);

  const handleDeleteTimeViewItem = (
    item: DepartmentCalendar | ScheduleWithDates,
    fieldName: 'departmentCalendars' | 'departmentSchedules'
  ) => {
    if (fieldName === 'departmentCalendars') {
      const currentDepartmentCalendars = departmentCalendars || [];
      const filteredCalendars = currentDepartmentCalendars.filter((a) =>
        item.id && item.id !== 0 ? a.id !== item.id : a.guid !== item.guid
      );
      setValue('departmentCalendars', filteredCalendars, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
    if (fieldName === 'departmentSchedules') {
      const currentSchedules: ScheduleWithDates[] = departmentSchedules || [];
      const filteredSchedules = currentSchedules.filter((a) =>
        item.id && item.id !== 0 ? a.id !== item.id : a.guid !== item.guid
      );
      setValue('departmentSchedules', filteredSchedules, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  };

  const handleTimeViewItemUpdate = (
    index: number,
    updated: any,
    fieldName: 'departmentCalendars' | 'departmentSchedules'
  ) => {
    if (fieldName === 'departmentCalendars') {
      const updatedCalendars = [...(departmentCalendars || [])];
      updatedCalendars[index] = updated;
      setValue('departmentCalendars', updatedCalendars, {
        shouldDirty: true,
        shouldValidate: true
      });
    }

    if (fieldName === 'departmentSchedules') {
      const updatedSchedules = [...(departmentSchedules || [])];
      updatedSchedules[index] = updated;
      setValue('departmentSchedules', updatedSchedules, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  };

  const handleAddTimeViewItem = async (
    fieldName: 'departmentCalendars' | 'departmentSchedules'
  ) => {
    if (fieldName === 'departmentCalendars') {
      const currentDepartmentCalendars = departmentCalendars || [];
      const newDepartmentCalendar = { ...DepartmentCalendarTemplate };
      newDepartmentCalendar.guid = generateGUID();
      if (id) {
        newDepartmentCalendar.departmentId = parseInt(id);
      }
      setValue(
        fieldName,
        [...currentDepartmentCalendars, newDepartmentCalendar],
        { shouldDirty: true, shouldValidate: true }
      );
    } else {
      const currentSchedule = departmentSchedules || [];

      const newDepartmentSchedule = { ...PickerScheduleTemplate };
      newDepartmentSchedule.guid = generateGUID();
      setValue(fieldName, [...currentSchedule, newDepartmentSchedule], {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  };

  useEffect(() => {
    function offset(el: HTMLElement) {
      var rect = el.getBoundingClientRect(),
        scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      return { top: rect.top + scrollTop };
    }

    if (treeRef.current !== null) {
      const treeOffset = offset(treeRef.current).top;
      setScrollHeight(`calc(100vh - ${treeOffset + 183}px)`);
      setHalfScrollHeight(`calc(50vh - ${treeOffset + 100}px)`);
    }
  }, []);

  const handleProductivityChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalDirty(true);

      setValue('isCustomProductivity', true, {
        shouldDirty: true,
        shouldValidate: true
      });

      setValue('productivity', e.target.value, {
        shouldDirty: true,
        shouldValidate: true
      });

      await trigger('productivity');
    },
    [setValue, trigger]
  );

  const handleIntervalDurationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalDirty(true);
      const value = e.target.value;

      setValue('intervalDuration', value === '' ? undefined : Number(value), {
        shouldDirty: true,
        shouldValidate: true
      });

      trigger('intervalDuration');
    },
    [setValue, trigger]
  );

  const calculateAverageProductivity = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      const departmentId = parseInt(id);
      const response =
        await DepartmentsService.getAverageProductivity(departmentId);

      if (response.data) {
        setLocalDirty(true);

        const productivityValue = response.data.toString();

        setValue('productivity', productivityValue, {
          shouldDirty: true,
          shouldValidate: true
        });

        setValue('isCustomProductivity', false, {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    } catch (error) {
      if (detailedInformationCardProps.onError) {
        detailedInformationCardProps.onError(
          'Не удалось рассчитать среднюю производительность'
        );
      }
    }
  }, [id, setValue, detailedInformationCardProps]);

  return (
    <FormProvider {...methods}>
      <div className="detailed-info-card" ref={treeRef}>
        <TabView>
          <TabPanel
            header="Основная"
            className={errors.label ? 'error-tab' : ''}
            contentStyle={{
              width: '100%',
              height: scrollHeight,
              overflow: 'auto'
            }}
          >
            <MainTab
              itemType={itemType}
              hasChildren={hasChildren}
              taskTypesOptions={taskTypesOptions}
              localTaskTypes={localTaskTypes}
              complexRoleOptions={complexRoleOptions}
              localUserRole={localUserRole}
              localUserRoleTags={localUserRoleTags}
              setLocalDirty={setLocalDirty}
              handleInputChange={handleInputChange}
              handleProductivityChange={handleProductivityChange}
              handleIntervalDurationChange={handleIntervalDurationChange}
              calculateAverageProductivity={calculateAverageProductivity}
              handleTskTypesSelectionChange={handleTskTypesSelectionChange}
              handleDeleteTaskType={handleDeleteTaskType}
              handleEditTaskType={handleEditTaskType}
              handleSelectionChange={handleSelectionChange}
              handleDeleteRole={handleDeleteRole}
            />
          </TabPanel>

          {itemType === 'Участок' && (
            <TabPanel
              header="Расположение"
              className={`detailedInfo-location-tab"  className="departmemt-location--tab ${
                locationTabErrors ? 'error-tab' : ''
              }`}
              contentStyle={{
                width: '100%',
                height: scrollHeight,
                overflow: 'auto'
              }}
            >
              <LocationTabContent
                isFias={isFias!}
                setValue={setValueDirty}
                register={register}
                watch={watch}
                errors={errors}
                houses={houses}
                fetchHouses={fetchHouses}
                inputsClassNames="detailed-card__input"
                errorsClassNames="detailed-card-errors"
                extraContent={
                  <>
                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ minWidth: '103.69px' }}>
                        Географические
                        <br />
                        зоны:
                      </label>
                      <CustomDropdown<ObjectForSelection>
                        multiplySelectMode
                        assetOptions={geographicalZonesOptions}
                        selectedAsset={geographicalZones}
                        onSelectionChange={handleGeographicalZonesChange}
                        placeholder={
                          geographicalZones.length === 0
                            ? 'Выберите географические зоны'
                            : `Выбрано географических зон: ${geographicalZones.length}`
                        }
                        emptyMessage="Географические зоны не найдены."
                        minWidth={415}
                        selectAllBtn
                        selectAllFromFiltered
                        passedClassName="w-full"
                        showDeletedWarning
                        deletedWarningTooltip="Некоторые географические зоны удалены в справочнике"
                      />
                    </div>

                    <div className="flex flex-row justify-content-between align-items-center gap-3">
                      <label style={{ minWidth: '103.69px' }}></label>
                      <TagsFrame
                        isEmpty={geographicalZones.length === 0}
                        emptyText="Географические зоны не выбраны"
                      >
                        <TagsComponent
                          initialTagsItems={markDeletedItems(
                            geographicalZones,
                            geographicalZonesOptions
                          )}
                          onDelete={handleDeleteGeographicalZoneTag}
                          onTagClick={onGeographicalZoneClick}
                        />
                      </TagsFrame>
                    </div>
                  </>
                }
              />
            </TabPanel>
          )}

          {itemType === 'Участок' && (
            <TabPanel
              header="Календари и расписания"
              className={calendarsAndSchedulesErrors ? 'error-tab' : ''}
            >
              <div
                style={{
                  width: '100%',
                  height: scrollHeight,
                  maxHeight: 'calc(100vh -265px)',
                  overflow: 'auto'
                }}
              >
                <div className="p-gap-2 department-calendars--wrapper">
                  <div
                    className="scrollable-container"
                    id="tags-scroll"
                    style={{
                      width: '100%',
                      height: 'fit-content',
                      maxHeight: `calc(${halfScrollHeight})`,
                      overflow: 'auto'
                    }}
                  >
                    <div
                      className="flex flex-column scrollable-content"
                      style={{ width: '99%' }}
                    >
                      <div className="p-d-flex p-flex-column p-gap-2 w-full">
                        <div className=" department-calendars">
                          <h3>Календари</h3>
                          {departmentCalendars?.map((calendar, index) => (
                            <TimeViewComponent
                              fieldPrefix="departmentCalendars"
                              index={index}
                              key={JSON.stringify(calendar)}
                              item={calendar}
                              options={calendarsOptions}
                              onDelete={() =>
                                handleDeleteTimeViewItem(
                                  calendar,
                                  'departmentCalendars'
                                )
                              }
                              onUpdate={(updated) =>
                                handleTimeViewItemUpdate(
                                  index,
                                  updated,
                                  'departmentCalendars'
                                )
                              }
                              errors={errors.departmentCalendars?.[index]}
                              passedClassName={''}
                              idField="calendarId"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="department-calendars__add flex flex-row justify-content-between align-items-center mb-3">
                    <label style={{ height: 'fit-content' }}>
                      {departmentCalendars?.length === 0 &&
                        'Календарь не выбран'}
                    </label>
                    <Button
                      icon="pi pi-plus"
                      onClick={() =>
                        handleAddTimeViewItem('departmentCalendars')
                      }
                      tooltip="Назначить календарь"
                    />
                  </div>
                </div>

                <div className="p-gap-2 department-schedules--wrapper">
                  <div
                    className="scrollable-container"
                    id="tags-scroll"
                    style={{
                      width: '100%',
                      height: 'fit-content',
                      maxHeight: `calc(${halfScrollHeight})`,
                      overflow: 'auto'
                    }}
                  >
                    <div
                      className="flex flex-column scrollable-content"
                      style={{ width: '99%' }}
                    >
                      <div className="p-d-flex p-flex-column p-gap-2 w-full">
                        <div className=" department-schedules">
                          <h3>Расписания</h3>
                          {departmentSchedules?.map((schedule, index) => (
                            <TimeViewComponent
                              fieldPrefix="departmentSchedules"
                              index={index}
                              key={JSON.stringify(schedule)}
                              item={schedule as ScheduleWithDates}
                              idField="scheduleId"
                              options={scheduleыOptions}
                              onDelete={() =>
                                handleDeleteTimeViewItem(
                                  schedule as ScheduleWithDates,
                                  'departmentSchedules'
                                )
                              }
                              onUpdate={(updated) =>
                                handleTimeViewItemUpdate(
                                  index,
                                  updated,
                                  'departmentSchedules'
                                )
                              }
                              errors={errors.departmentSchedules?.[index]}
                              passedClassName={''}
                              placeholder="Выберите расписание"
                              fieldTranslate={{
                                directTranslate: 'расписание',
                                tooltip: 'расписания'
                              }}
                              useDateOnlyFormat
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="department-schedules__add flex flex-row justify-content-between align-items-center mb-3">
                    <label style={{ height: 'fit-content' }}>
                      {(!departmentSchedules ||
                        departmentSchedules?.length === 0) &&
                        'Расписание не назначено'}
                    </label>
                    <Button
                      icon="pi pi-plus"
                      onClick={() =>
                        handleAddTimeViewItem('departmentSchedules')
                      }
                      tooltip="Назначить расписание"
                    />
                  </div>
                </div>

                <div className="p-gap-2 department-employee-schedule--wrapper">
                  <h3>Распределение нарядов на сотрудников</h3>
                  <div className="department-employee-schedule__settings flex flex-row justify-content-between align-items-center mb-3">
                    <label
                      style={{ height: 'fit-content' }}
                      className={
                        errors.useEmployeeAssignmentSchedule
                          ? 'text-red-500'
                          : ''
                      }
                    >
                      {employeeAssignmentScheduleStatusText}
                    </label>
                    <Button
                      icon="pi pi-cog"
                      onClick={() =>
                        setShowEmployeeAssignmentScheduleDialog(true)
                      }
                      tooltip="Настроить расписание распределения нарядов на сотрудников"
                    />
                  </div>
                </div>
              </div>
            </TabPanel>
          )}
        </TabView>

        <div className="button-with-tab">
          <div className="flex gap-3">
            <Button
              className="functional-structure-save-btn__roles"
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit, (e) => console.log(e))}
            />
          </div>
        </div>
        <TaskTypeEffortDialog
          visible={isTaskTypeEffortDialogOpen}
          onHide={() => setIsTaskTypeEffortDialogOpen(false)}
          taskTypeId={editingTaskTypeId}
          taskTypes={taskTypes}
          onSaveWeight={handleSaveWeight}
        />

        {itemType === 'Участок' && (
          <DepartmentAssignmentScheduleDialog
            visible={showEmployeeAssignmentScheduleDialog}
            onCancel={() => setShowEmployeeAssignmentScheduleDialog(false)}
            onSave={handleEmployeeAssignmentScheduleSave}
            departmentAssignmentSchedule={
              employeeAssignmentScheduleSetting ?? null
            }
            enabled={useEmployeeAssignmentSchedule ?? false}
            header="Настройка расписания распределения нарядов на сотрудников"
            scheduleEnabledLabel="Распределение нарядов на сотрудников без оптимизации расстояния"
            fetchNextRunDateTime={fetchEmployeeAssignmentNextRun}
          />
        )}
      </div>
    </FormProvider>
  );
};

DetailedInformationCard.displayName = 'DetailedInformationCard';

export default DetailedInformationCard;
